(ns app.front.view.map-common.util
  {:clj-kondo/config '{:linters {:refer {:level :off}}}}
  (:require ["ol/extent" :as ol-extent]
            ["ol/Feature" :default Feature]
            ["ol/geom" :refer (Point Polygon MultiLineString MultiPolygon)]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(defn debug [& args]
  (when ^boolean js/goog.DEBUG
    (apply js/console.log args)))


;; The cluster source in @cloudpermit/map-component groups pins within 40px of each
;; other. Fitting the view to a cluster's extent separates its pins only when they
;; end up further apart than that (with some margin) at the deepest zoom the fit is
;; allowed to use.
(def ^:private cluster-separation-px 60)
(def ^:private max-cluster-fit-zoom 20)


(defn click-handler-fn
  "Map singleclick handler. Cluster clicks, in order: a resolved cluster-feature-fx
   (legacy: opens something derived from the cluster); a separable cluster fits the
   view to its members; a non-separable cluster with the opt-in cluster-results-fx
   lists the cluster's records; a single feature opens its record; a click on no feature
   calls the opt-in point-click-fx; otherwise a point search or a plain zoom-in."
  [{:keys [single-feature-fx
           cluster-feature-fx
           cluster-results-fx
           point-click-fx
           search-success-evt
           search-not-found-evt
           do-point-search?
           cluster-layer-id
           max-cluster-zoom
           max-cluster-count-for-fx
           layer-id-fn
           recenter-on-selection?]}]
  (fn [{:keys [db]} [_ ^js event {:keys [:group/id :municipality/number]}]]
    (debug event)
    (let [coords                    (.-coordinate event)
          ol-map                    (.-map event)
          click-layer-ids           (or (some->> (when (ifn? layer-id-fn)
                                                   (layer-id-fn db))
                                                 (remove nil?)
                                                 not-empty)
                                        (->> [cluster-layer-id]
                                             (remove nil?)
                                             not-empty))
          features                  (-> ol-map
                                        (.getFeaturesAtPixel (.-pixel event)
                                                             #js {:hitTolerance 0
                                                                  :layerFilter  (fn [^js layer]
                                                                                  (some #{(.get layer "id")}
                                                                                        click-layer-ids))})
                                        js->clj)
          _                         (debug "features" (clj->js features))
          cluster?                  (some #(some-> (.get ^js % "features") count (> 1)) features)
          ^js single-feature        (and (not cluster?)
                                         (= 1 (count features))
                                         (some-> (.get ^js (first features) "features") (aget 0)))
          _                         (debug "single-feature" single-feature)
          view                      (.getView ol-map)
          current-zoom              (.getZoom view)
          cluster-features          (when cluster?
                                      (->> features
                                           (mapcat #(.get ^js % "features"))))
          cluster-extent            (when cluster?
                                      (reduce ol-extent/extend
                                              (ol-extent/createEmpty)
                                              (keep (fn [^js feature]
                                                      (some-> feature .getGeometry .getExtent))
                                                    cluster-features)))
          separable-cluster?        (when cluster-extent
                                      (> (max (ol-extent/getWidth cluster-extent)
                                              (ol-extent/getHeight cluster-extent))
                                         (* cluster-separation-px
                                            (.getResolutionForZoom view max-cluster-fit-zoom))))
          large-cluster?            (and cluster?
                                         (< current-zoom (or max-cluster-zoom 20))
                                         (or (nil? max-cluster-count-for-fx)
                                             (> (count cluster-features) max-cluster-count-for-fx)))
          cluster-fx-result         (when (and cluster?
                                               cluster-feature-fx
                                               (not large-cluster?))
                                      (cluster-feature-fx db cluster-features))
          cluster-results-fx-result (when (and cluster?
                                               cluster-results-fx
                                               (not separable-cluster?))
                                      (cluster-results-fx db cluster-features))
          single-feature-fx-result  (when (and single-feature single-feature-fx)
                                      (single-feature-fx db single-feature))
          point-click-fx-result     (when (and point-click-fx (empty? features))
                                      (point-click-fx db coords))]
      (debug "view" view)
      (debug "current-zoom" current-zoom)
      (cond
        cluster-fx-result
        (merge cluster-fx-result
               {::zoom-in {:view          view
                           :coords        (ol-extent/getCenter cluster-extent)
                           :zoom-increase (if (and max-cluster-zoom
                                                   (< current-zoom max-cluster-zoom))
                                            2.0
                                            0.0)}})

        ;; Large or unresolved legacy clusters first fit the view to their members.
        ;; Non-separable clusters continue to the opt-in records panel, point
        ;; search, or plain zoom behavior below.
        (and cluster? separable-cluster?)
        {::fit-cluster-extent (util/mmap view cluster-extent)}

        cluster-results-fx-result
        (cond-> cluster-results-fx-result
          recenter-on-selection?
          (assoc ::recenter {:view   view
                             :coords (ol-extent/getCenter cluster-extent)}))

        single-feature-fx-result
        (cond-> single-feature-fx-result
          recenter-on-selection?
          (assoc ::recenter {:view   view
                             :coords (or (some-> single-feature .getGeometry .getCoordinates)
                                         coords)}))

        point-click-fx-result
        point-click-fx-result

        (and do-point-search? id)
        {:db           (assoc-in db [::data ::point-search-loading?] true)
         :comm/command {:command          :address/point-search
                        :data             (p/assoc-if
                                            {:domain-group-id id
                                             :coordinates     coords
                                             :property-types  [:property.type/property]
                                             :sourceCRS       "3857"}
                                            :municipality    number)
                        :on-success       [search-success-evt coords]
                        :on-not-found     [search-not-found-evt coords]
                        :finally-dispatch [::map-click.done]}}

        ;; Non-separable cluster on a map without point search: keep the original
        ;; zoom-in behavior so the click still does something.
        cluster?
        {::zoom-in (util/mmap {:zoom-increase 1.0}
                              view
                              coords)}))))


(rf/reg-event-db ::map-click.done
  (fn [db _]
    (assoc-in db [::data ::point-search-loading?] false)))


(rf/reg-fx ::zoom-in
  (fn [{:keys [^js view coords zoom-increase]}]
    (doto view
      (.setCenter coords)
      (.setZoom (+ zoom-increase (.getZoom view))))))


(rf/reg-fx ::recenter
  (fn [{:keys [^js view coords]}]
    (.setCenter view coords)))


(rf/reg-fx ::fit-cluster-extent
  (fn [{:keys [^js view cluster-extent]}]
    (.fit view cluster-extent #js {:maxZoom  max-cluster-fit-zoom
                                   :padding  #js [50 50 50 50]
                                   :duration 250})))


(rf/reg-sub ::point-search-loading?
  #(-> % ::data ::point-search-loading?))


(defn map-extent->polygon [^js ol-map]
  (let [[minx miny maxx maxy] (.calculateExtent (.getView ol-map) (.getSize ol-map))]
    [[[minx miny]
      [minx maxy]
      [maxx maxy]
      [maxx miny]
      [minx miny]]]))

(defn point-feature [coords]
  (Feature.
    #js {:geometry (Point. coords) :name (str "Point-" (rand-int 100))}))

(defn polygon-feature [coords]
  (Feature. #js {:geometry (Polygon. (clj->js coords))
                 :boundary true}))

(defn line-feature [coords]
  (Feature. #js {:geometry (MultiLineString. (clj->js coords))
                 :boundary true}))

(defn multi-polygon-feature [coords]
  (Feature. #js {:geometry (MultiPolygon. (clj->js coords))
                 :boundary true}))


(defn fit-map
  ([src view]
   (fit-map src view nil))
  ([^js src ^js view {:keys [padding max-zoom]}]
   (let [source-extent (.getExtent src)]
     (when-not (some #(#{js/Infinity 0} (.abs js/Math %)) source-extent)
       (.fit view source-extent #js {:maxZoom (or max-zoom 20)
                                     :padding (or (some-> padding clj->js)
                                                  #js [20 20 20 20])})))))
